import { ReactNode, useContext, useMemo } from "react";
import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { getPanelAppearanceTable, StyledPanel } from "./StyledPanel";
import { PanelAppearance } from "./PanelAppearance";
import { PanelContext } from "./PanelContext";
import { TabbedContainer, TabbedContainerProps } from "../TabbedContainer";
import { createComponentStyleHook } from "../../tokens";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";

/** The `Panel` component properties. */
export interface PanelProps extends BaseProps {

  /** Specifies the panel appearance. If not specified defaults to "auto". */
  readonly appearance?: PanelAppearance;

  /** The panel aria role. */
  readonly role?: string;

  /** The component children. */
  readonly children?: ReactNode;
}

const useStyles = createComponentStyleHook<PanelProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "panel",
});

/**
 * Gets the level of the currently containing panel.
 * @returns The containing panel level.
 */
export function usePanelLevel(): "none" | Omit<PanelAppearance, "auto"> {
  const parentPanelContext = useContext(PanelContext);

  if (parentPanelContext.level === -1) return "none";
  if (parentPanelContext.level === 0) return "primary";

  return "secondary";
}

/** A simple container panel. */
export const Panel = (props: PanelProps) => {
  const { children, role } = props;
  const { mode, styles, StyleModeBoundary } = useStyles(props);
  let { appearance = "auto" } = props;

  const parentPanelLevel = usePanelLevel();

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: ["panel"],
    testIdPrefix: "panel",
  });

  if (appearance === "auto") {
    if (parentPanelLevel === "none") appearance = "primary";
    else appearance = "secondary";
  }

  const contextData = useMemo(() => {
    if (appearance === "primary") return { level: 0 };

    return { level: 1 };
  }, [appearance]);

  return (
    <StyleModeBoundary mode={mode}>
      <PanelContext.Provider value={contextData}>
        <StyledPanel
          styles={styles}
          appearance={getPanelAppearanceTable(styles)[appearance]}
          role={role}
          style={styles}
          {...baseHtmlProps}>
          {children}
        </StyledPanel>
      </PanelContext.Provider>
    </StyleModeBoundary>
  );
};

/** A variant of the `Panel` component which has tabs. */
const TabbedPanel = (props: TabbedContainerProps & PanelProps) => {
  const { className, testId, style, appearance, role, children, ...tabbedContainerProps } = props;
  const { mode, styles, StyleModeBoundary } = useStyles(props);

  const baseHtmlProps = getHtmlPropsFromBaseProps({ className, testId, style }, {
    className: [styles.TabbedPanel, "tabbed-panel"],
  });

  return (
    <StyleModeBoundary mode={mode}>
      <Panel {...baseHtmlProps} appearance={appearance} role={role}>
        <TabbedContainer {...tabbedContainerProps} className={styles.TabbedContainer}>
          {children}
        </TabbedContainer>
      </Panel>
    </StyleModeBoundary>
  );
};

/** An individual pane in the tabbed panel. */
TabbedPanel.TabPane = TabbedContainer.TabPane;

/** A variant of the `Panel` component which has tabs. */
Panel.Tabbed = TabbedPanel;
